import { Utility, isInsideViewport as utilityIsInsideViewport } from '../utility/utility_controller'
import { getColumnsToAnimate } from './shared/getColumnsToAnimate'
import { sortByVisual } from './shared/sortByVisual'
import { getStaggerDelayMs } from './shared/geatEasedStaggerDelay'
import { normalizeStaggerTargets, getNormalizedStaggerIndex, getNormalizedStaggerLength } from './shared/normalizeStaggerTargets'
import { SCOPES, getScopeRoot, getScopeForIsProjectOverlay, getNavigationScopeForType } from '../scope/scope'

/**
 * "Sync with Navigation Animation" runtime resolver.
 *
 * For each element flagged with `data-lay-sync-nav-anim="1"`, if its
 * host `.col` is part of the nav-anim's stagger cascade
 * (`stagger-up` / `stagger-down`) at page load, we:
 *
 *   1. Compute the SLOT delay the column gets in that cascade.
 *   2. Write that delay (plus duration / easing / top-trigger) onto
 *      the element's own scroll- / text-animation data-attrs, so the
 *      per-element engine plays its animation at the synced time.
 *   3. Tag the column with `col._laySyncMute = true` so the nav-anim's
 *      stagger tween uses zero translateY / no opacity change for it
 *      — the column stays in the cascade (its slot index counts toward
 *      the rhythm of neighbouring cols), but the actual visual is the
 *      per-element animation, not the nav-anim's default translate+fade.
 *
 * Must run BEFORE `scroll_animations_controller.i()` and any
 * text-animation init, and BEFORE the nav-anim controller's transition_in
 * builds its timeline — `_laySyncMute` is read by the value functions
 * inside that timeline.
 */

// 60fps engine — scroll-animation stores frame counts. The nav-anim
// runs in ms (anime.js), so we convert when writing scroll-anim attrs.
const FRAME_MS = 1000 / 60
const msToFrames = (ms) => Math.max(0, Math.round(ms / FRAME_MS))
const syncDebugEnabled = () => typeof window !== 'undefined' && window.__LAY_ANIM_DEBUG === true
const syncLog = function () {
	if (!syncDebugEnabled()) return
	console.log.apply(console, arguments)
}

// Lay Options nav-anim modes that produce a stagger cascade. Sidebar
// hides the checkbox in non-stagger modes, but defensive check here in
// case data shipped with sync=true while a different mode was selected.
const STAGGER_MODES = { 'stagger-up': 1, 'stagger-down': 1, 'stagger-fade': 1 }

function isInsideViewport(el, yOffset) {
	// Reuse the utility helper if it accepts the same shape, else fall
	// back to a simple top/bottom rect check. utility's signature is
	// `isInsideViewport(el)` without offset, so we do our own here to
	// match `getColumnsToAnimate`'s `yOffset` semantic.
	const rect = el.getBoundingClientRect()
	const vh = window.innerHeight || document.documentElement.clientHeight
	return rect.bottom > yOffset && rect.top < vh
}

function getYOffsetForMode(mode) {
	// Match `getColumnsToAnimate`'s yOffset logic — only stagger-up
	// applies a non-zero offset.
	if (mode !== 'stagger-up') return 0
	const ld = window.layData || {}
	return Utility && Utility.isPhoneSize
		? parseInt(ld.misc_options_scroll_transition_offset_mobile, 10) || 0
		: parseInt(ld.misc_options_scroll_transition_offset_desktop, 10) || 0
}

function resolveSyncWithNavAnim(scope) {
	const ld = window.layData || {}
	const mode = ld.navigation_transition_in
	// Resolve scope root to confine queries — without this, an overlay-
	// scope event running this function would scan the entire DOM (page
	// underlay + overlay) and match flags in the wrong scope's cascade.
	const scopeRoot = scope ? getScopeRoot(scope) : null
	const queryRoot = scopeRoot || document
	syncLog('[lay-sync] resolve start, mode =', mode, 'scope =', scope || 'unscoped')
	if (!mode || !STAGGER_MODES[mode]) {
		syncLog('[lay-sync] no stagger mode → bail')
		return
	}
	const navDurationMs = parseInt(ld.navigation_transition_in_duration, 10) || 1000
	const navEasing = typeof ld.navigation_transition_in_easing === 'string'
		? ld.navigation_transition_in_easing
		: 'easeOutCubic'
	let staggerMs = parseInt(ld.navigation_transition_in_stagger, 10)
	if (!isFinite(staggerMs) || staggerMs < 0) staggerMs = 100

	// Find every sync-flagged element in the current scope.
	const syncEls = Array.prototype.slice.call(
		queryRoot.querySelectorAll('[data-lay-sync-nav-anim="1"]'),
	)
	if (syncEls.length === 0) return

	const yOffset = getYOffsetForMode(mode)

	// Map sync element → host column. Drop those whose host col is off
	// screen — they wouldn't be part of the nav-anim cascade anyway, so
	// sync has nothing to attach to. Stack-as-one: walking up still
	// finds a parent `.col`; the lookup below handles both the inner
	// col and the stack col case.
	const targets = []
	for (let i = 0; i < syncEls.length; i++) {
		const el = syncEls[i]
		const col = el.closest && el.closest('.col')
		if (!col) continue
		if (!isInsideViewport(col, yOffset)) continue
		targets.push({ el, col })
	}
	if (targets.length === 0) return
	syncLog('[lay-sync] targets in viewport:', targets.length)
	let mutedCount = 0

	// Mirror nav-anim's column-selection + sort pass. jQuery is loaded
	// globally on the frontend (it's a peer dep of the theme); use it
	// here because `getColumnsToAnimate` calls `.each()` on the input.
	const jq = window.jQuery
	if (typeof jq !== 'function') return
	// Cascade column query — when called for the overlay scope, look in
	// `#lay-project-overlay>.lay-content` instead of `body>.lay-content`,
	// so sync flags inside an overlay can find their host columns. The
	// default (unscoped / NORMAL_PAGE) preserves the legacy selector.
	const colSelector = scope === SCOPES.PROJECT_OVERLAY
		? '#lay-project-overlay>.lay-content .col:not(.type-stack), #lay-project-overlay>.lay-content .col.type-stack .col'
		: 'body>.lay-content .col:not(.type-stack), body>.lay-content .col.type-stack .col'
	const $columns = jq(colSelector)
	const colsInViewport = getColumnsToAnimate($columns, mode, 'transition_in')
	if (!Array.isArray(colsInViewport) || colsInViewport.length === 0) return

	if (mode.indexOf('stagger') !== -1 && colsInViewport.length > 1) {
		sortByVisual(colsInViewport, mode)
		normalizeStaggerTargets(colsInViewport)
	}
	// Col cascade has its own stagger context. UI fade targets (site
	// title, menu, etc.) cascade independently and in parallel — see
	// fadeIn_SiteTitle_MenuBar_Menu_etc.js. So the sync col's slot
	// index is just its position within `colsInViewport`, no UI offset.
	syncLog('[lay-sync] cascade has', colsInViewport.length, 'cols')

	// Build a Set for O(1) lookups + a Map of col → index for the slot
	// computation. Stack-as-one members hit the inner col in `targets`
	// but the cascade list holds the outer stack col — walk up until we
	// find a match.
	const colIndex = new Map()
	for (let i = 0; i < colsInViewport.length; i++) colIndex.set(colsInViewport[i], getNormalizedStaggerIndex(colsInViewport[i], i))
	const length = getNormalizedStaggerLength(colsInViewport[0], colsInViewport.length)

	for (const t of targets) {
		let idx = colIndex.has(t.col) ? colIndex.get(t.col) : -1
		if (idx === -1) {
			let node = t.col.parentElement
			while (node && idx === -1) {
				if (node.classList && node.classList.contains('col') && colIndex.has(node)) {
					idx = colIndex.get(node)
					t.col = node // remember the real cascade col for muting below
				}
				node = node.parentElement
			}
		}
		if (idx === -1) {
			syncLog('[lay-sync] skip: col not in cascade list', {
				colClasses: (t.col.className || '').slice(0, 100),
			})
			continue
		}

		// Mirror nav-anim's delay formula EXACTLY: same `getStaggerDelayMs`
		// helper, same `staggerMs` from Lay Options, same easing name.
		// Col cascade has its own stagger context (no UI offset); UI
		// fades cascade independently in parallel.
		const delayMs = getStaggerDelayMs(idx, length, staggerMs, navEasing)
		const isText = t.el.classList.contains('lay-text-animation') || t.el.classList.contains('lay-build-anim-text-disabled')
		const isScroll = t.el.hasAttribute('data-lay-scroll-anim')
		syncLog('[lay-sync] target:', {
			kind: isText ? 'text' : isScroll ? 'scroll' : 'unknown',
			slotIdx: idx,
			slotOfTotal: length,
			delayMs: Math.round(delayMs),
			navDurationMs,
			navEasing,
			tag: t.el.tagName + '.' + (t.el.className || '').split(' ').filter(Boolean).slice(0, 3).join('.'),
		})
		applySyncToElement(t.el, delayMs, navDurationMs, navEasing)
		// Tag the host col so nav-anim's stagger tween skips its visual
		// (translateY + opacity), leaving the per-element animation to
		// do the visible work. The col stays in the cascade so the
		// neighbours' delay computation isn't shifted.
		t.col._laySyncMute = true
		mutedCount++
	}
	syncLog('[lay-sync] done, muted', mutedCount, 'of', targets.length, 'targets')
}

function applySyncToElement(el, delayMs, navDurationMs, navEasing) {
	// Scroll-animation wrap: PHP emits `data-lay-scroll-anim="1"` on it.
	if (el.hasAttribute('data-lay-scroll-anim')) {
		// Force Trigger & Play — scrub would fight the nav-anim cascade.
		el.setAttribute('data-lay-scroll-anim-scrub', '0')
		el.setAttribute('data-lay-scroll-anim-trigger-top', '100')
		el.setAttribute('data-lay-scroll-anim-duration-frames', String(msToFrames(navDurationMs)))
		el.setAttribute('data-lay-scroll-anim-delay-frames', String(msToFrames(delayMs)))
		el.setAttribute('data-lay-scroll-anim-easing', navEasing)
		return
	}
	// Text-animation root: PHP marked it with `lay-text-animation` class.
	if (el.classList.contains('lay-text-animation') || el.classList.contains('lay-build-anim-text-disabled')) {
		el.setAttribute('data-delay', String(Math.round(delayMs)))
		el.setAttribute('data-animation-easing', navEasing)
		el.setAttribute('data-top', '100')
		// Duration intentionally NOT touched — user / build-anim group
		// owns it. The cascade sync only borrows the start time.
		return
	}
}

// Flip the gate on every flagged target + re-fire playAnimation for any
// that the IO observer already tried while we were holding it. Called
// when nav-anim emits `transition_navigation_in_started` (= the nav-anim
// timeline is about to play). After this, text- and scroll-anim engines
// run at the same time origin as the nav-anim cascade.
function releaseDeferredSyncPlays(scope) {
	syncLog('[lay-sync] nav-anim started — releasing deferred plays', {
		t: performance.now().toFixed(1),
		scope: scope || 'unscoped',
	})
	const scopeRoot = scope ? getScopeRoot(scope) : null
	const queryRoot = scopeRoot || document
	const syncEls = queryRoot.querySelectorAll('[data-lay-sync-nav-anim="1"]')
	for (let i = 0; i < syncEls.length; i++) {
		const el = syncEls[i]
		el._laySyncFired = true
		// Text-anim path: the IO observer's playAnimation call was bailed
		// because `_laySyncFired` was false. If `_laySyncWaitingForNavStart`
		// was set, re-invoke the global `playAnimation` (exposed on the
		// textanimations module via window.laytheme?). Simpler: dispatch
		// a re-entry by calling the registered re-play hook below.
		if (el._laySyncWaitingForNavStart && typeof window.layReplayDeferredTextAnimation === 'function') {
			window.layReplayDeferredTextAnimation(el)
		}
	}
}

const sync_nav_anim_to_elements_controller = {
	resolve: resolveSyncWithNavAnim,
	i: function () {
		if (window.laytheme && typeof window.laytheme.on === 'function') {
			// Subscribe BEFORE navigation_animations_controller.i() — that
			// runs later in app.js, so my `transition_in` listener fires
			// first. By running resolveSyncWithNavAnim from `transition_in`
			// the col layout is already settled (the same event drives
			// nav-anim's getColumnsToAnimate pass), so col counts and
			// in-viewport checks return real values. Running it
			// synchronously at .i() time gave 144 cols because images
			// hadn't laid out yet — every col was still at top:0.
			window.laytheme.on('transition_in', (type) => resolveSyncWithNavAnim(getNavigationScopeForType(type)))
			window.laytheme.on('newpageshown', (layoutObj, type, obj, isProjectOverlayObj) => {
				const scope = getScopeForIsProjectOverlay(isProjectOverlayObj)
				resolveSyncWithNavAnim(scope)
				// Safety fallback for setups where nav-anim never fires
				// `transition_navigation_in_started`:
				//   • `navigation_transition_in === 'none'`
				//   • `revealing_transition_on_first_visit` off (= DEFAULT)
				//     on first page load
				// In those cases the deferred gate would stay closed
				// forever and sync-with-nav-anim text/scroll elements
				// would never play. After a short grace window for the
				// nav-anim signal to arrive, release unconditionally —
				// elements animate at "now" which is a sensible
				// fallback when there's no cascade to sync against.
				// If `transition_navigation_in_started` fires first,
				// the second release is a no-op (idempotent).
				setTimeout(() => releaseDeferredSyncPlays(scope), 1500)
			})
			// Release the sync-defer gate when nav-anim's timeline plays —
			// same time origin as the col cascade.
			window.laytheme.on('transition_navigation_in_started', (type) => releaseDeferredSyncPlays(getNavigationScopeForType(type)))
		}
	},
}

export default sync_nav_anim_to_elements_controller
